import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import client from "@/lib/api";
import { PrimaryBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import {
  fmtDate, fmtDateTime, timeAgo, dueLabel, DOC_STATUS_STYLES, suggestedUpdate,
} from "@/lib/format";
import {
  ArrowLeft, MapPin, Package, Clock, User, Send, Plus, Copy, CheckCircle2,
  FileText, MessageSquare, StickyNote, History, ShieldCheck, Lock, RotateCcw, ClipboardCheck, Square, CheckSquare,
  AlertTriangle, CalendarDays, CheckCheck, Pencil, UploadCloud, Eye, Trash2, Paperclip, Share2, Ship, Anchor,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

function DocManifestRow({ doc, statuses, disabled, uploading, onUpload, onView, onRemove, onStatus }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const st = DOC_STATUS_STYLES[doc.status] || {};
  const file = doc.file;
  const tid = doc.type.replace(/\s+/g, "-");
  const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
  const handleDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (disabled) return;
    const f = e.dataTransfer.files?.[0];
    if (f) onUpload(f);
  };
  return (
    <div data-testid={`doc-row-${tid}`} className="border border-line rounded-sm p-3"
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setDrag(true); }}
      onDragLeave={() => setDrag(false)} onDrop={handleDrop}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex items-center gap-2">
          <span className="text-[13.5px] text-ink font-medium truncate">{doc.type}</span>
          <span className="shrink-0 text-[9px] font-black tracking-widest uppercase px-1.5 py-0.5 rounded border" style={{ color: st.fg, backgroundColor: st.bg, borderColor: st.fg }}>{doc.status}</span>
        </div>
        <Select value={doc.status} onValueChange={onStatus} disabled={disabled}>
          <SelectTrigger data-testid={`doc-status-${tid}`} className="h-8 w-[130px] shrink-0 rounded-sm border-line text-[12px]" style={{ color: st.fg }}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s} className="text-[12.5px]">{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <input ref={inputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,image/png,image/jpeg,application/pdf" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }} />
      {file ? (
        <div className="mt-2.5 flex items-center justify-between gap-2 bg-surface-alt border border-line rounded-sm px-3 py-2">
          <div className="min-w-0 flex items-center gap-2">
            <Paperclip className="h-3.5 w-3.5 text-ink-3 shrink-0" />
            <span className="text-[12.5px] text-ink truncate font-mono-data">{file.filename}</span>
            <span className="text-[11px] text-ink-3 shrink-0">{fmtSize(file.size)}</span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button data-testid={`doc-view-${tid}`} onClick={onView} className="h-7 px-2 rounded-sm border border-line text-ink-2 hover:text-ink hover:border-steel text-[11.5px] font-medium flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> View</button>
            {!disabled && <button data-testid={`doc-remove-${tid}`} onClick={onRemove} className="h-7 w-7 rounded-sm border border-danger/40 text-danger hover:bg-danger/[0.08] flex items-center justify-center" title="Remove file"><Trash2 className="h-3.5 w-3.5" /></button>}
          </div>
        </div>
      ) : (
        <button type="button" disabled={disabled || uploading} onClick={() => inputRef.current?.click()}
          data-testid={`doc-upload-${tid}`}
          className={`mt-2.5 w-full border-2 border-dashed rounded-sm py-3 flex flex-col items-center justify-center gap-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${drag ? "border-warn bg-warn/[0.08]" : "border-warn/60 bg-[#FFFDF9] hover:bg-[#FFF9EC]"}`}>
          <UploadCloud className="h-5 w-5 text-warn" />
          <span className="text-[12.5px] font-bold text-ink">{uploading ? "Uploading…" : "Drag & drop or click to upload"}</span>
          <span className="text-[10.5px] font-mono-data text-ink-3">PDF, PNG, JPG · up to 10MB</span>
        </button>
      )}
    </div>
  );
}


const CLOSEOUT_ITEMS = [
  ["proof_of_delivery_received", "Proof of delivery received"],
  ["final_customer_update_sent", "Final customer update sent"],
  ["required_documents_complete", "Required documents complete"],
  ["internal_notes_complete", "Internal notes complete"],
  ["billing_handoff_complete", "Billing handoff complete"],
  ["manager_review_complete", "Manager review complete"],
];

const NA_STATE_STYLE = {
  Completed: { fg: "#059669", bg: "#ECFDF5", label: "Completed" },
  Overdue: { fg: "#DC2626", bg: "#FEF2F2", label: "Overdue" },
  Open: { fg: "#64748B", bg: "#F1F5F9", label: "Open" },
};

const CLOSE_ACTION_LABEL = {
  proof_of_delivery_received: "Mark POD Received",
  final_customer_update_sent: "Mark Final Update Sent",
  required_documents_complete: "Complete Required Documents",
  internal_notes_complete: "Mark Internal Notes Complete",
  billing_handoff_complete: "Mark Billing Handoff Complete",
  manager_review_complete: "Mark Manager Review Complete",
};

function NextActionBadge({ state }) {
  if (!state || state === "None") return null;
  const s = NA_STATE_STYLE[state] || NA_STATE_STYLE.Open;
  return (
    <span data-testid={`next-action-state-${state.toLowerCase()}`}
      className="inline-flex items-center gap-1.5 rounded-sm font-medium px-2 py-0.5 text-[11px]"
      style={{ color: s.fg, backgroundColor: s.bg }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: s.fg }} />
      {s.label}
    </span>
  );
}

function toInputDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toISOString().slice(0, 10);
}

function Section({ title, icon: Icon, label, children, action, sid, highlight }) {
  return (
    <div
      data-section={sid}
      className={`bg-surface border rounded-md overflow-hidden transition-all duration-300 ${
        highlight ? "border-warn ring-2 ring-warn/30" : "border-line"
      }`}
    >
      <div className="px-5 py-3 border-b border-line flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="font-heading text-[14.5px] font-semibold text-ink">{title}</h3>
          {label && <span className="op-label">{label}</span>}
        </div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="op-label mb-1">{label}</div>
      <div className="text-[13.5px] text-ink">{children}</div>
    </div>
  );
}

const DEST_TYPES = ["Hotel / Resort", "Residential", "Commercial", "Warehouse", "Retail Store", "Other"];

const RECEIVER_NOTICE = "📣 NOTICE: If the Primary Purchasing Manager is unavailable, any authorized team member below can sign for and clear this cargo from the pier.";

function waHref(phone) {
  const digits = (phone || "").replace(/[^0-9]/g, "");
  return digits ? `https://wa.me/${digits}` : null;
}

function ReceiverRow({ label, name, title, phone, testid }) {
  const wa = waHref(phone);
  return (
    <div data-testid={testid} className="flex justify-between items-center bg-surface-alt p-2.5 rounded-sm border border-line">
      <div>
        <p className="text-[13px] font-bold text-ink">{name} <span className="text-[10px] font-mono text-ink-3 uppercase tracking-wider">({label})</span></p>
        {title && <p className="text-[11px] text-ink-3 font-medium">{title}</p>}
      </div>
      {wa && (
        <a data-testid={`${testid}-wa`} href={wa} target="_blank" rel="noreferrer"
          className="bg-[#059669] text-white text-[10px] font-bold px-2.5 py-1.5 rounded-sm hover:bg-[#047857] transition-colors shrink-0">
          WhatsApp
        </a>
      )}
    </div>
  );
}

function MaritimeSection({ job, id, disabled, onSaved }) {
  const FIELDS = ["vessel_name", "departure_sailing_time", "dock_terminal_slot"];
  const toLocalInput = (v) => {
    if (!v) return "";
    const d = new Date(v);
    if (isNaN(d.getTime())) return "";
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
  };
  const [form, setForm] = useState(() => ({
    vessel_name: job.vessel_name || "",
    departure_sailing_time: toLocalInput(job.departure_sailing_time),
    dock_terminal_slot: job.dock_terminal_slot || "",
  }));
  const [saving, setSaving] = useState(false);
  const norm = (k) => (k === "departure_sailing_time" ? toLocalInput(job[k]) : (job[k] || ""));
  const dirty = FIELDS.some((k) => (form[k] || "") !== norm(k));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        vessel_name: form.vessel_name,
        dock_terminal_slot: form.dock_terminal_slot,
        departure_sailing_time: form.departure_sailing_time ? new Date(form.departure_sailing_time).toISOString() : "",
      };
      const r = await client.put(`/shipments/${id}`, payload);
      onSaved(r.data);
      toast.success("Vessel details saved");
    } catch (e) {
      toast.error("Could not save vessel details.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Section title="Maritime / Vessel Transit" icon={Ship}>
      <div className="space-y-3.5">
        <div>
          <label className="op-label block mb-1.5">Vessel name</label>
          <input data-testid="mar-vessel" disabled={disabled} value={form.vessel_name} onChange={(e) => set("vessel_name", e.target.value)}
            className="w-full h-9 px-3 rounded-sm border border-line bg-surface text-sm focus:outline-none focus:ring-1 focus:ring-brand disabled:bg-surface-alt" placeholder="MV Island Trader" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="op-label block mb-1.5">Departure sailing time</label>
            <input data-testid="mar-sailing" type="datetime-local" disabled={disabled} value={form.departure_sailing_time} onChange={(e) => set("departure_sailing_time", e.target.value)}
              className="w-full h-9 px-3 rounded-sm border border-line bg-surface text-sm focus:outline-none focus:ring-1 focus:ring-brand disabled:bg-surface-alt" />
          </div>
          <div>
            <label className="op-label block mb-1.5">Dock / Terminal handover slot</label>
            <input data-testid="mar-dock" disabled={disabled} value={form.dock_terminal_slot} onChange={(e) => set("dock_terminal_slot", e.target.value)}
              className="w-full h-9 px-3 rounded-sm border border-line bg-surface text-sm focus:outline-none focus:ring-1 focus:ring-brand disabled:bg-surface-alt" placeholder="Berth 4 · 06:00–08:00" />
          </div>
        </div>
        {dirty && !disabled && (
          <button data-testid="mar-save" onClick={save} disabled={saving}
            className="h-9 px-4 rounded-sm bg-brand text-white text-[13px] font-medium hover:bg-brand-dark transition-colors disabled:opacity-60">
            {saving ? "Saving…" : "Save vessel details"}
          </button>
        )}
      </div>
    </Section>
  );
}

function FinalMileSection({ job, id, disabled, onSaved }) {
  const FIELDS = [
    "destination_property", "destination_type", "access_instructions", "end_recipient_owner",
    "primary_contact_name", "primary_contact_title", "primary_contact_phone",
    "backup_contact_name", "backup_contact_title", "backup_contact_phone",
  ];
  const initial = () => {
    const base = Object.fromEntries(FIELDS.map((k) => [k, job[k] || ""]));
    // Migrate legacy single on-site contact into the Primary slot if empty.
    if (!base.primary_contact_name && job.onsite_contact_name) base.primary_contact_name = job.onsite_contact_name;
    if (!base.primary_contact_phone && job.onsite_contact_phone) base.primary_contact_phone = job.onsite_contact_phone;
    return base;
  };
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const dirty = FIELDS.some((k) => (form[k] || "") !== (job[k] || ""));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const trackUrl = job.track_token ? `${window.location.origin}/track/${job.track_token}` : null;
  const save = async () => {
    setSaving(true);
    try {
      const r = await client.put(`/shipments/${id}`, form);
      onSaved(r.data);
      toast.success("Delivery details saved");
    } catch (e) {
      toast.error("Could not save delivery details.");
    } finally {
      setSaving(false);
    }
  };
  const hasPrimary = !!form.primary_contact_name;
  const hasBackup = !!form.backup_contact_name;
  return (
    <Section title="Final-Mile & Property Access" icon={MapPin}>
      <div className="space-y-3.5">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="op-label block mb-1.5">Destination property</label>
            <input data-testid="fm-property" disabled={disabled} value={form.destination_property} onChange={(e) => set("destination_property", e.target.value)}
              className="w-full h-9 px-3 rounded-sm border border-line bg-surface text-sm focus:outline-none focus:ring-1 focus:ring-brand disabled:bg-surface-alt" placeholder="Blue Waters Resort" />
          </div>
          <div>
            <label className="op-label block mb-1.5">Destination type</label>
            <Select value={form.destination_type || undefined} onValueChange={(v) => set("destination_type", v)} disabled={disabled}>
              <SelectTrigger data-testid="fm-desttype" className="h-9 border-line rounded-sm text-[13px]"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>{DEST_TYPES.map((t) => <SelectItem key={t} value={t} className="text-[13px]">{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <label className="op-label block mb-1.5">Access instructions</label>
          <textarea data-testid="fm-access" rows={2} disabled={disabled} value={form.access_instructions} onChange={(e) => set("access_instructions", e.target.value)}
            className="w-full px-3 py-2 rounded-sm border border-line bg-surface text-sm focus:outline-none focus:ring-1 focus:ring-brand resize-none disabled:bg-surface-alt" placeholder="Use northern delivery gate; guard has manifest copy." />
        </div>
        <div>
          <label className="op-label block mb-1.5">End recipient / owner</label>
          <input data-testid="fm-end-recipient" disabled={disabled} value={form.end_recipient_owner} onChange={(e) => set("end_recipient_owner", e.target.value)}
            className="w-full h-9 px-3 rounded-sm border border-line bg-surface text-sm focus:outline-none focus:ring-1 focus:ring-brand disabled:bg-surface-alt" placeholder="e.g., Smith Estate (Villa #12), Warehouse Dock B, Main Pavilion Kitchen" />
        </div>

        {/* Primary receiver */}
        <div className="pt-1">
          <div className="op-label mb-1.5">Primary contact</div>
          <div className="grid grid-cols-3 gap-3">
            <input data-testid="fm-primary-name" disabled={disabled} value={form.primary_contact_name} onChange={(e) => set("primary_contact_name", e.target.value)}
              className="w-full h-9 px-3 rounded-sm border border-line bg-surface text-sm focus:outline-none focus:ring-1 focus:ring-brand disabled:bg-surface-alt" placeholder="Name" />
            <input data-testid="fm-primary-title" disabled={disabled} value={form.primary_contact_title} onChange={(e) => set("primary_contact_title", e.target.value)}
              className="w-full h-9 px-3 rounded-sm border border-line bg-surface text-sm focus:outline-none focus:ring-1 focus:ring-brand disabled:bg-surface-alt" placeholder="Title / Role" />
            <input data-testid="fm-primary-phone" inputMode="tel" enterKeyHint="next" disabled={disabled} value={form.primary_contact_phone} onChange={(e) => set("primary_contact_phone", e.target.value)}
              className="w-full h-9 px-3 rounded-sm border border-line bg-surface text-sm focus:outline-none focus:ring-1 focus:ring-brand disabled:bg-surface-alt" placeholder="+1 268 555 1234" />
          </div>
        </div>

        {/* Backup receiver */}
        <div>
          <div className="op-label mb-1.5">Backup contact</div>
          <div className="grid grid-cols-3 gap-3">
            <input data-testid="fm-backup-name" disabled={disabled} value={form.backup_contact_name} onChange={(e) => set("backup_contact_name", e.target.value)}
              className="w-full h-9 px-3 rounded-sm border border-line bg-surface text-sm focus:outline-none focus:ring-1 focus:ring-brand disabled:bg-surface-alt" placeholder="Name" />
            <input data-testid="fm-backup-title" disabled={disabled} value={form.backup_contact_title} onChange={(e) => set("backup_contact_title", e.target.value)}
              className="w-full h-9 px-3 rounded-sm border border-line bg-surface text-sm focus:outline-none focus:ring-1 focus:ring-brand disabled:bg-surface-alt" placeholder="Title / Role" />
            <input data-testid="fm-backup-phone" inputMode="tel" enterKeyHint="done" disabled={disabled} value={form.backup_contact_phone} onChange={(e) => set("backup_contact_phone", e.target.value)}
              className="w-full h-9 px-3 rounded-sm border border-line bg-surface text-sm focus:outline-none focus:ring-1 focus:ring-brand disabled:bg-surface-alt" placeholder="+1 268 555 5678" />
          </div>
        </div>

        {/* Authorized Property Receivers preview */}
        {hasPrimary && (
          <div className="pt-1 space-y-2.5" data-testid="fm-receivers">
            <span className="op-label block">Authorized property receivers</span>
            {hasBackup && (
              <div data-testid="fm-receiver-notice" className="bg-amber-50 border border-amber-200 rounded-sm p-2 text-[11px] font-semibold text-[#D97706]">{RECEIVER_NOTICE}</div>
            )}
            <ReceiverRow testid="fm-receiver-primary" label="Primary" name={form.primary_contact_name} title={form.primary_contact_title} phone={form.primary_contact_phone} />
            {hasBackup && <ReceiverRow testid="fm-receiver-backup" label="Backup" name={form.backup_contact_name} title={form.backup_contact_title} phone={form.backup_contact_phone} />}
          </div>
        )}

        {dirty && !disabled && (
          <button data-testid="fm-save" onClick={save} disabled={saving}
            className="h-9 px-4 rounded-sm bg-brand text-white text-[13px] font-medium hover:bg-brand-dark transition-colors disabled:opacity-60">
            {saving ? "Saving…" : "Save delivery details"}
          </button>
        )}
        {trackUrl && (
          <div className="pt-3 border-t border-line">
            <div className="op-label mb-1.5 flex items-center gap-1.5"><Share2 className="h-3.5 w-3.5" /> Shareable driver / tracking link</div>
            <div className="flex gap-2">
              <div data-testid="fm-track-link" className="h-9 px-3 flex-1 flex items-center rounded-sm border border-line bg-surface-alt text-[12px] font-mono-data text-ink truncate">{trackUrl}</div>
              <button data-testid="fm-copy-track" onClick={() => { navigator.clipboard?.writeText(trackUrl); toast.success("Tracking link copied"); }}
                className="h-9 px-3 rounded-sm border border-line text-ink-2 hover:text-ink hover:border-steel flex items-center gap-1.5 text-[12.5px] shrink-0"><Copy className="h-3.5 w-3.5" /> Copy</button>
              <a data-testid="fm-open-track" href={trackUrl} target="_blank" rel="noreferrer"
                className="h-9 px-3 rounded-sm border border-line text-ink-2 hover:text-ink hover:border-steel flex items-center gap-1.5 text-[12.5px] shrink-0"><Eye className="h-3.5 w-3.5" /> Open</a>
            </div>
            <p className="text-[11px] text-ink-3 mt-1.5">Public, read-only. Safe to share in a WhatsApp group with the driver.</p>
          </div>
        )}
      </div>
    </Section>
  );
}


export default function ShipmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const focus = searchParams.get("focus");
  const [highlight, setHighlight] = useState(null);
  const [job, setJob] = useState(null);
  const [meta, setMeta] = useState({ stages: [], risk_statuses: [], doc_statuses: [] });
  const [users, setUsers] = useState([]);
  const [note, setNote] = useState("");
  const [updateMsg, setUpdateMsg] = useState("");
  const [naEdit, setNaEdit] = useState("");
  const [naResolve, setNaResolve] = useState("none"); // none | edit | due
  const [dueEdit, setDueEdit] = useState("");

  const load = () => client.get(`/shipments/${id}`).then((r) => { setJob(r.data); setNaEdit(r.data.next_action || ""); });
  useEffect(() => {
    load();
    client.get("/meta").then((r) => setMeta(r.data));
    client.get("/users").then((r) => setUsers(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!job || !focus) return;
    const el = document.querySelector(`[data-section="${focus}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlight(focus);
      const t = setTimeout(() => setHighlight(null), 3000);
      return () => clearTimeout(t);
    }
  }, [job, focus]);

  const patch = async (changes, msg) => {
    try {
      const r = await client.put(`/shipments/${id}`, changes);
      setJob(r.data);
      if (msg) toast.success(msg);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed.");
    }
  };

  const toggleChecklist = async (item, value) => {
    try {
      const r = await client.put(`/shipments/${id}/closeout-checklist`, { item, value });
      setJob(r.data);
    } catch (e) {
      toast.error("Could not update checklist.");
    }
  };

  const closeJob = async () => {
    try {
      const r = await client.post(`/shipments/${id}/close`, {});
      setJob(r.data);
      toast.success("Job closed.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not close job.");
    }
  };

  const reopenJob = async () => {
    try {
      const r = await client.post(`/shipments/${id}/reopen`, {});
      setJob(r.data);
      toast.success("Job reopened");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reopen job.");
    }
  };

  const setDoc = async (type, status) => {
    try {
      const r = await client.put(`/shipments/${id}/documents/${encodeURIComponent(type)}`, { status });
      setJob(r.data);
    } catch (e) {
      toast.error("Could not update document.");
    }
  };

  const [uploadingDoc, setUploadingDoc] = useState(null);
  const uploadDoc = async (type, file) => {
    if (!file) return;
    const okTypes = ["application/pdf", "image/png", "image/jpeg", "image/webp"];
    if (!okTypes.includes(file.type)) { toast.error("Only PDF, PNG, or JPG files are allowed."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("File exceeds the 10MB limit."); return; }
    setUploadingDoc(type);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await client.post(`/shipments/${id}/documents/${encodeURIComponent(type)}/upload`, fd);
      setJob(r.data);
      toast.success(`${type} uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed.");
    } finally {
      setUploadingDoc(null);
    }
  };
  const viewDoc = async (type) => {
    try {
      const r = await client.get(`/shipments/${id}/documents/${encodeURIComponent(type)}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Could not open file.");
    }
  };
  const removeDoc = async (type) => {
    try {
      const r = await client.delete(`/shipments/${id}/documents/${encodeURIComponent(type)}/file`);
      setJob(r.data);
      toast.success("File removed");
    } catch (e) {
      toast.error("Could not remove file.");
    }
  };

  const markPodReceived = async () => {
    await setDoc("Proof of Delivery", "Received");
    await toggleChecklist("proof_of_delivery_received", true);
  };

  const resolveClose = (b) => {
    if (b.action === "mark_pod") return markPodReceived();
    if (b.action === "clear_risk") return patch({ risk_status: "On Track" }, "Risk status cleared");
    if (b.action === "toggle") return toggleChecklist(b.item, true);
    if (b.action === "resolve_next_action") { setNaEdit(job.next_action || ""); setNaResolve(naResolve.startsWith("close") ? "none" : "close-edit"); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      const r = await client.post(`/shipments/${id}/notes`, { body: note });
      setJob(r.data);
      setNote("");
      toast.success("Note added");
    } catch (e) {
      toast.error("Could not add note.");
    }
  };

  const sendUpdate = async () => {
    if (!updateMsg.trim()) return;
    try {
      const r = await client.post(`/shipments/${id}/customer-updates`, { message: updateMsg });
      setJob(r.data);
      setUpdateMsg("");
      toast.success("Customer update logged");
    } catch (e) {
      toast.error("Could not send update.");
    }
  };

  if (!job)
    return <Layout><div className="text-sm text-ink-3 font-mono-data py-20 text-center">Loading shipment…</div></Layout>;

  const tmpl = meta.update_templates || {};
  const suggestion = (job.risk_status === "Delayed" ? tmpl["Delayed"] : tmpl[job.current_stage]) || suggestedUpdate(job.current_stage, job.risk_status);
  const pod = job.documents.find((d) => d.type === "Proof of Delivery");
  const closed = job.current_stage === "Closed";
  const canReopen = user?.role === "admin" || user?.role === "manager";

  return (
    <Layout>
      <button onClick={() => navigate(-1)} data-testid="detail-back-btn" className="flex items-center gap-1.5 text-[13px] text-steel hover:text-brand transition-colors mb-4">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {/* Header */}
      <div className="bg-surface border border-line rounded-md p-5 mb-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="font-mono-data text-lg font-semibold text-ink">{job.job_number}</span>
              <PrimaryBadge job={job} size="md" />
            </div>
            <h1 className="font-heading text-xl font-semibold text-ink mt-1.5">{job.customer_name}</h1>
            <div className="flex items-center gap-2 text-[13px] text-ink-2 mt-1.5">
              {job.pickup_location} → {job.delivery_location}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-x-8 gap-y-2.5">
            <Field label="Current Status">{job.current_stage}</Field>
            <Field label="Owner">{job.assigned_owner || "—"}</Field>
            <Field label="Next Action"><span className="text-ink-2">{job.next_action || "—"}</span></Field>
            <Field label="Due">{job.next_action_due_date ? <span className={dueLabel(job.next_action_due_date).includes("overdue") ? "text-danger font-medium" : ""}>{dueLabel(job.next_action_due_date)}</span> : "—"}</Field>
          </div>
        </div>
        {job.issues.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-4 pt-4 border-t border-line">
            {job.issues.map((i, idx) => (
              <span key={idx} className="text-[11.5px] px-2 py-0.5 rounded-sm border border-warn/50 bg-warn/10 text-warn font-medium">{i}</span>
            ))}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-5 min-w-0">
          {/* Overview details */}
          <Section title="Shipment Overview" icon={Package}>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
              <Field label="Pickup Details">{job.pickup_location}</Field>
              <Field label="Delivery Details">{job.delivery_location}</Field>
              <Field label="Cargo Details">{job.cargo_description}</Field>
              <Field label="Delivery Due">{fmtDate(job.delivery_due_date)}</Field>
              <Field label="Created">{fmtDate(job.created_at)}</Field>
              <Field label="Last Updated">{timeAgo(job.last_updated_at)}</Field>
            </div>
          </Section>

          {/* Documents — maritime workspaces get a single delivery-verification photo; overland keeps the full manifest grid */}
          {meta.enable_interisland_transit ? (
            <Section title="Delivery Verification & Proof" icon={FileText} sid="documents" highlight={highlight === "documents"}
              label={job.documents.find((d) => d.type === "Delivery Drop-Off Proof Photo" && d.file) ? "Captured" : "Awaiting photo"}>
              {(() => {
                const dv = job.documents.find((d) => d.type === "Delivery Drop-Off Proof Photo");
                const hasPhoto = dv && dv.file;
                return (
                  <div className="space-y-3" data-testid="delivery-verification">
                    {hasPhoto ? (
                      <div className="flex items-center justify-between gap-3 border-2 border-[#0B132B] rounded-md p-3 bg-white shadow-[2px_2px_0px_0px_#0B132B]">
                        <div className="flex items-center gap-2 min-w-0">
                          <CheckCircle2 className="h-5 w-5 text-[#059669] shrink-0" />
                          <div className="min-w-0">
                            <p className="text-[13px] font-bold text-ink truncate">{dv.file.filename || "Delivery photo"}</p>
                            <p className="text-[11px] text-ink-3">Captured {dv.file.uploaded_at ? timeAgo(dv.file.uploaded_at) : ""}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button data-testid="dv-view" onClick={() => viewDoc("Delivery Drop-Off Proof Photo")}
                            className="h-9 px-3 rounded-sm border border-line text-ink-2 hover:text-ink hover:border-steel flex items-center gap-1.5 text-[12.5px]"><Eye className="h-3.5 w-3.5" /> View</button>
                          {!closed && (
                            <button data-testid="dv-remove" onClick={() => removeDoc("Delivery Drop-Off Proof Photo")}
                              className="h-9 px-3 rounded-sm border border-line text-danger hover:border-danger flex items-center gap-1.5 text-[12.5px]"><Trash2 className="h-3.5 w-3.5" /> Remove</button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <label data-testid="dv-capture" className={`block border-2 border-dashed border-[#D97706] rounded-md p-6 text-center bg-[#FFFDF9] ${closed ? "opacity-60 pointer-events-none" : "cursor-pointer hover:bg-[#FFF9F0]"}`}>
                        <input type="file" accept="image/*" capture="environment" className="hidden" disabled={closed || uploadingDoc === "Delivery Drop-Off Proof Photo"}
                          onChange={(e) => uploadDoc("Delivery Drop-Off Proof Photo", e.target.files?.[0])} />
                        <UploadCloud className="h-7 w-7 text-[#D97706] mx-auto mb-1.5" />
                        <p className="text-[13px] font-bold text-[#0B132B]">{uploadingDoc === "Delivery Drop-Off Proof Photo" ? "Uploading…" : "Take delivery photo"}</p>
                        <p className="text-[11px] text-ink-3 mt-0.5">Opens the camera on mobile · saves one proof-of-delivery image</p>
                      </label>
                    )}
                  </div>
                );
              })()}
            </Section>
          ) : (
            <Section title="Manifests & PODs" icon={FileText} sid="documents" highlight={highlight === "documents"} label={`${job.documents.filter((d) => ["Received", "Uploaded"].includes(d.status)).length}/${job.documents.length} ready`}>
              <div className="space-y-2.5">
                {job.documents.map((d) => (
                  <DocManifestRow
                    key={d.type}
                    doc={d}
                    statuses={meta.doc_statuses}
                    disabled={closed}
                    uploading={uploadingDoc === d.type}
                    onUpload={(file) => uploadDoc(d.type, file)}
                    onView={() => viewDoc(d.type)}
                    onRemove={() => removeDoc(d.type)}
                    onStatus={(v) => setDoc(d.type, v)}
                  />
                ))}
              </div>
            </Section>
          )}

          {/* Final-Mile & Property Access — maritime workspaces only (overland stays streamlined) */}
          {meta.enable_interisland_transit && (
            <FinalMileSection job={job} id={id} disabled={closed} onSaved={setJob} />
          )}

          {/* Customer Updates */}
          <Section title="Customer Updates" icon={MessageSquare} sid="customer-updates" highlight={highlight === "customer-updates"} label={`Next due ${fmtDate(job.customer_update_due_date)}`}>
            <div className="rounded-sm border border-line bg-surface-alt p-3 mb-3">
              <div className="flex items-center justify-between mb-1.5">
                <span className="op-label">Suggested update · {job.current_stage}</span>
                <button data-testid="copy-suggestion-btn" onClick={() => { setUpdateMsg(suggestion); }} className="text-[12px] text-steel hover:text-brand flex items-center gap-1 transition-colors">
                  <Copy className="h-3 w-3" /> Use
                </button>
              </div>
              <p className="text-[13px] text-ink-2 leading-relaxed">{suggestion}</p>
            </div>
            <textarea
              data-testid="customer-update-textarea"
              value={updateMsg}
              onChange={(e) => setUpdateMsg(e.target.value)}
              rows={3}
              placeholder="Write a customer-facing update…"
              className="w-full rounded-sm border border-line bg-surface p-3 text-[13.5px] focus:outline-none focus:ring-1 focus:ring-brand resize-none"
            />
            <div className="flex justify-end mt-2">
              <button data-testid="send-update-btn" onClick={sendUpdate} className="h-9 px-4 rounded-sm bg-brand text-white text-[13px] font-medium flex items-center gap-1.5 hover:bg-brand-dark transition-colors">
                <Send className="h-3.5 w-3.5" /> Log Update
              </button>
            </div>
            {job.customer_updates.length > 0 && (
              <div className="mt-4 space-y-2.5">
                <p className="op-label">Update history</p>
                {[...job.customer_updates].reverse().map((u) => (
                  <div key={u.id} className="border-l-2 border-line pl-3">
                    <p className="text-[13px] text-ink leading-relaxed">{u.message}</p>
                    <p className="text-[11px] text-ink-3 mt-0.5 font-mono-data">{u.sent_by} · {fmtDateTime(u.sent_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Internal Notes */}
          <Section title="Internal Notes" icon={StickyNote}>
            <div className="flex gap-2">
              <input
                data-testid="note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addNote()}
                placeholder="Add an internal note…"
                className="flex-1 h-9 px-3 rounded-sm border border-line bg-surface text-[13.5px] focus:outline-none focus:ring-1 focus:ring-brand"
              />
              <button data-testid="add-note-btn" onClick={addNote} className="h-9 px-3 rounded-sm border border-line bg-surface-alt text-ink text-[13px] font-medium hover:bg-line/40 transition-colors flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> Add
              </button>
            </div>
            {job.internal_notes.length > 0 && (
              <div className="mt-3 space-y-2.5">
                {[...job.internal_notes].reverse().map((nt) => (
                  <div key={nt.id} className="bg-surface-alt border border-line rounded-sm p-3">
                    <p className="text-[13px] text-ink">{nt.body}</p>
                    <p className="text-[11px] text-ink-3 mt-1 font-mono-data">{nt.author} · {fmtDateTime(nt.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>

        {/* Right column */}
        <div className="space-y-5 min-w-0">
          {/* Job Controls / Closeout / POD — back-office; overland workspaces only */}
          {!meta.enable_interisland_transit && (<>
          {/* Controls */}
          <Section title="Job Controls" icon={ShieldCheck} sid="controls" highlight={highlight === "controls"}>
            <div className="space-y-3.5">
              <div>
                <label className="op-label block mb-1.5">Current Stage</label>
                <Select value={job.current_stage} disabled={closed} onValueChange={(v) => patch({ current_stage: v }, `Moved to ${v}`)}>
                  <SelectTrigger data-testid="detail-stage-select" className="h-9 border-line rounded-sm text-[13px]"><SelectValue /></SelectTrigger>
                  <SelectContent>{meta.stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="op-label block mb-1.5">Assigned Owner</label>
                <Select value={job.assigned_owner} disabled={closed} onValueChange={(v) => patch({ assigned_owner: v }, `Assigned to ${v}`)}>
                  <SelectTrigger data-testid="detail-owner-select" className="h-9 border-line rounded-sm text-[13px]"><SelectValue placeholder="Assign owner" /></SelectTrigger>
                  <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="op-label block mb-1.5">Risk Status</label>
                <Select value={job.risk_status} disabled={closed} onValueChange={(v) => patch({ risk_status: v }, `Risk set to ${v}`)}>
                  <SelectTrigger data-testid="detail-risk-select" className="h-9 border-line rounded-sm text-[13px]"><SelectValue /></SelectTrigger>
                  <SelectContent>{meta.risk_statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="op-label">Next Action</label>
                  <NextActionBadge state={job.next_action_state} />
                </div>
                <div className="flex gap-2">
                  <input
                    data-testid="detail-next-action-input"
                    value={naEdit}
                    disabled={closed}
                    onChange={(e) => setNaEdit(e.target.value)}
                    className="flex-1 h-9 px-3 rounded-sm border border-line bg-surface text-[13px] focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-60"
                  />
                  <button data-testid="save-next-action-btn" disabled={closed} onClick={() => patch({ next_action: naEdit }, "Next action updated")} className="h-9 px-3 rounded-sm bg-brand text-white text-[12.5px] font-medium hover:bg-brand-dark transition-colors disabled:opacity-50">Save</button>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <CalendarDays className="h-3.5 w-3.5 text-steel shrink-0" />
                  <input
                    type="date"
                    data-testid="next-action-due-input"
                    value={toInputDate(job.next_action_due_date)}
                    disabled={closed}
                    onChange={(e) => patch({ next_action_due_date: e.target.value ? `${e.target.value}T17:00:00+00:00` : null }, "Due date updated")}
                    className="h-8 px-2 rounded-sm border border-line bg-surface text-[12.5px] focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-60"
                  />
                  {job.next_action_due_date && (
                    <span className={`text-[12px] ${job.next_action_state === "Overdue" ? "text-danger font-medium" : "text-ink-3"}`}>{dueLabel(job.next_action_due_date)}</span>
                  )}
                </div>
                {job.next_action && !closed && (
                  <div className="mt-2">
                    {job.next_action_state === "Completed" ? (
                      <button data-testid="reopen-next-action-btn" onClick={() => patch({ next_action_status: "Open" }, "Next action reopened")}
                        className="h-8 px-3 rounded-sm border border-line bg-surface text-[12.5px] font-medium text-ink-2 hover:text-ink hover:border-steel transition-colors flex items-center gap-1.5">
                        <RotateCcw className="h-3.5 w-3.5" /> Reopen Action
                      </button>
                    ) : (
                      <button data-testid="complete-next-action-btn" onClick={() => patch({ next_action_status: "Completed" }, "Next action marked complete")}
                        className="h-8 px-3 rounded-sm bg-ok text-white text-[12.5px] font-medium hover:opacity-90 transition-opacity flex items-center gap-1.5">
                        <CheckCheck className="h-3.5 w-3.5" /> Mark Next Action Complete
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="pt-2 border-t border-line">
                <label className="op-label block mb-1.5">Closeout Status</label>

                {/* Ready-to-Close blocker panel */}
                {job.current_stage === "Delivered" && (job.rtc_blockers || []).length > 0 && (
                  <div data-testid="rtc-blocker-panel" className="rounded-sm border border-warn/40 bg-[#E9EEF3] p-3 mb-3">
                    <div className="flex items-center gap-1.5 text-warn text-[13px] font-semibold">
                      <AlertTriangle className="h-4 w-4" /> Blocked from Ready to Close
                    </div>
                    <ul className="mt-2 space-y-1">
                      {job.rtc_blockers.map((b) => (
                        <li key={b.code} data-testid={`blocker-${b.code}`} className="text-[12.5px] text-ink-2 flex items-start gap-1.5">
                          <span className="text-warn leading-5">•</span> {b.message}
                        </li>
                      ))}
                    </ul>

                    {/* Resolve overdue next action inline */}
                    {job.rtc_blockers.some((b) => b.code === "next_action_overdue") && (
                      <div data-testid="resolve-next-action" className="mt-3 pt-3 border-t border-warn/30 space-y-2.5">
                        <Field label="Next action">{job.next_action || "—"}</Field>
                        <Field label="Due date"><span className="text-danger font-medium">{dueLabel(job.next_action_due_date)}</span></Field>
                        <Field label="Assigned owner">{job.assigned_owner || "—"}</Field>

                        {naResolve === "edit" && (
                          <div className="flex gap-2">
                            <input data-testid="resolve-na-input" value={naEdit} onChange={(e) => setNaEdit(e.target.value)}
                              className="flex-1 h-8 px-2 rounded-sm border border-line bg-surface text-[12.5px] focus:outline-none focus:ring-1 focus:ring-brand" />
                            <button data-testid="resolve-na-save" onClick={() => patch({ next_action: naEdit }, "Next action updated").then(() => setNaResolve("none"))}
                              className="h-8 px-3 rounded-sm bg-brand text-white text-[12px] font-medium hover:bg-brand-dark">Save</button>
                          </div>
                        )}
                        {naResolve === "due" && (
                          <div className="flex gap-2">
                            <input type="date" data-testid="resolve-due-input" value={dueEdit} onChange={(e) => setDueEdit(e.target.value)}
                              className="flex-1 h-8 px-2 rounded-sm border border-line bg-surface text-[12.5px] focus:outline-none focus:ring-1 focus:ring-brand" />
                            <button data-testid="resolve-due-save" disabled={!dueEdit} onClick={() => patch({ next_action_due_date: `${dueEdit}T17:00:00+00:00` }, "Due date updated").then(() => setNaResolve("none"))}
                              className="h-8 px-3 rounded-sm bg-brand text-white text-[12px] font-medium hover:bg-brand-dark disabled:opacity-50">Save</button>
                          </div>
                        )}

                        <div className="flex flex-wrap gap-2 pt-0.5">
                          <button data-testid="resolve-complete-btn" onClick={() => patch({ next_action_status: "Completed" }, "Next action marked complete")}
                            className="h-8 px-3 rounded-sm bg-ok text-white text-[12px] font-medium hover:opacity-90 transition-opacity flex items-center gap-1.5">
                            <CheckCheck className="h-3.5 w-3.5" /> Mark Complete
                          </button>
                          <button data-testid="resolve-edit-btn" onClick={() => { setNaEdit(job.next_action || ""); setNaResolve(naResolve === "edit" ? "none" : "edit"); }}
                            className="h-8 px-3 rounded-sm border border-line bg-surface text-[12px] font-medium text-ink-2 hover:text-ink hover:border-steel transition-colors flex items-center gap-1.5">
                            <Pencil className="h-3.5 w-3.5" /> Edit Next Action
                          </button>
                          <button data-testid="resolve-due-btn" onClick={() => { setDueEdit(toInputDate(job.next_action_due_date)); setNaResolve(naResolve === "due" ? "none" : "due"); }}
                            className="h-8 px-3 rounded-sm border border-line bg-surface text-[12px] font-medium text-ink-2 hover:text-ink hover:border-steel transition-colors flex items-center gap-1.5">
                            <CalendarDays className="h-3.5 w-3.5" /> Change Due Date
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-[13.5px] text-ink">{job.closeout_status}</span>
                  {job.current_stage === "Delivered" && (
                    <button
                      data-testid="move-ready-btn"
                      onClick={() => patch({ current_stage: "Ready to Close" }, "Moved to Ready to Close")}
                      className="h-8 px-3 rounded-sm bg-brand text-white text-[12.5px] font-medium flex items-center gap-1.5 hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!job.closeout_eligible}
                      title={job.closeout_eligible ? "" : job.close_reason}
                    >
                      <ClipboardCheck className="h-3.5 w-3.5" /> Move to Ready to Close
                    </button>
                  )}
                </div>
              </div>
            </div>
          </Section>

          {/* Closeout */}
          <Section title="Closeout" icon={ClipboardCheck} sid="closeout" highlight={highlight === "closeout"} label={closed ? "Closed" : job.closeout_status}>
            {closed ? (
              <div className="space-y-3">
                <div className="rounded-sm bg-surface-alt border border-line p-3 space-y-2">
                  <div className="flex items-center gap-1.5 text-ok text-[13px] font-medium">
                    <Lock className="h-3.5 w-3.5" /> This job is closed and locked.
                  </div>
                  <div className="grid grid-cols-2 gap-y-2 gap-x-4 pt-1">
                    <Field label="Closed Date">{fmtDateTime(job.closed_at)}</Field>
                    <Field label="Closed By">{job.closed_by || "—"}</Field>
                    <Field label="Final Status">Closed</Field>
                    <Field label="Proof of Delivery">{pod?.status}</Field>
                    <Field label="Final Customer Update">{job.customer_updates.length ? "Sent" : "Not sent"}</Field>
                    <Field label="Billing Handoff">{job.closeout_checklist?.billing_handoff_complete ? "Complete" : "Pending"}</Field>
                  </div>
                  {job.closeout_note && (
                    <div className="pt-1">
                      <div className="op-label mb-1">Closeout Note</div>
                      <p className="text-[13px] text-ink-2">{job.closeout_note}</p>
                    </div>
                  )}
                </div>
                {canReopen && (
                  <button data-testid="reopen-job-btn" onClick={reopenJob} className="w-full h-9 rounded-sm border border-line bg-surface text-ink text-[13px] font-medium hover:bg-surface-alt transition-colors flex items-center justify-center gap-1.5">
                    <RotateCcw className="h-3.5 w-3.5" /> Reopen Job
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-1">
                {CLOSEOUT_ITEMS.map(([key, label]) => {
                  const checked = !!job.closeout_checklist?.[key];
                  return (
                    <button
                      key={key}
                      data-testid={`closeout-item-${key}`}
                      onClick={() => toggleChecklist(key, !checked)}
                      className="w-full flex items-center gap-2.5 py-2 text-left hover:bg-surface-alt rounded-sm px-1 transition-colors"
                    >
                      {checked
                        ? <CheckSquare className="h-4 w-4 text-ok shrink-0" strokeWidth={2} />
                        : <Square className="h-4 w-4 text-ink-3 shrink-0" strokeWidth={1.75} />}
                      <span className={`text-[13px] ${checked ? "text-ink" : "text-ink-2"}`}>{label}</span>
                    </button>
                  );
                })}
                <div className="pt-3 mt-1 border-t border-line">
                  {(job.close_blockers || []).length === 0 ? (
                    <div data-testid="close-ready-panel" className="rounded-sm border border-ok/40 bg-[#E9EEF3] p-3 mb-3 flex items-center gap-1.5 text-ok text-[13px] font-medium">
                      <CheckCircle2 className="h-4 w-4" /> Ready for final close.
                    </div>
                  ) : (
                    <div data-testid="close-blocker-panel" className="rounded-sm border border-warn/40 bg-[#E9EEF3] p-3 mb-3">
                      <div className="flex items-center gap-1.5 text-warn text-[13px] font-semibold">
                        <AlertTriangle className="h-4 w-4" /> Blocked from Closing
                      </div>
                      <div className="mt-2.5 space-y-2.5">
                        {job.close_blockers.map((b) => (
                          <div key={b.code} data-testid={`close-blocker-${b.code}`} className="border-b border-warn/20 last:border-0 pb-2.5 last:pb-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="text-[13px] text-ink font-medium">{b.name}</div>
                                <div className="text-[11.5px] text-ink-2 mt-0.5">{b.status}{b.owner ? ` · ${b.owner}` : ""}</div>
                              </div>
                              {b.action === "mark_pod" && (
                                <button data-testid={`close-action-${b.code}`} onClick={() => resolveClose(b)} className="shrink-0 h-7 px-2.5 rounded-sm bg-ok text-white text-[11.5px] font-medium hover:opacity-90 transition-opacity">{CLOSE_ACTION_LABEL[b.item]}</button>
                              )}
                              {b.action === "clear_risk" && (
                                <button data-testid={`close-action-${b.code}`} onClick={() => resolveClose(b)} className="shrink-0 h-7 px-2.5 rounded-sm bg-brand text-white text-[11.5px] font-medium hover:bg-brand-dark transition-colors">Clear Risk Status</button>
                              )}
                              {b.action === "toggle" && (
                                <button data-testid={`close-action-${b.code}`} onClick={() => resolveClose(b)} className="shrink-0 h-7 px-2.5 rounded-sm bg-ok text-white text-[11.5px] font-medium hover:opacity-90 transition-opacity">{CLOSE_ACTION_LABEL[b.item]}</button>
                              )}
                            </div>
                            {b.action === "resolve_next_action" && (
                              <div className="mt-2 space-y-2">
                                {naResolve === "close-edit2" && (
                                  <div className="flex gap-2">
                                    <input data-testid="close-na-input" value={naEdit} onChange={(e) => setNaEdit(e.target.value)} className="flex-1 h-7 px-2 rounded-sm border border-line bg-surface text-[12px] focus:outline-none focus:ring-1 focus:ring-brand" />
                                    <button data-testid="close-na-save" onClick={() => patch({ next_action: naEdit }, "Next action updated").then(() => setNaResolve("none"))} className="h-7 px-2.5 rounded-sm bg-brand text-white text-[11.5px] font-medium hover:bg-brand-dark">Save</button>
                                  </div>
                                )}
                                {naResolve === "close-due2" && (
                                  <div className="flex gap-2">
                                    <input type="date" data-testid="close-due-input" value={dueEdit} onChange={(e) => setDueEdit(e.target.value)} className="flex-1 h-7 px-2 rounded-sm border border-line bg-surface text-[12px] focus:outline-none focus:ring-1 focus:ring-brand" />
                                    <button data-testid="close-due-save" disabled={!dueEdit} onClick={() => patch({ next_action_due_date: `${dueEdit}T17:00:00+00:00` }, "Due date updated").then(() => setNaResolve("none"))} className="h-7 px-2.5 rounded-sm bg-brand text-white text-[11.5px] font-medium hover:bg-brand-dark disabled:opacity-50">Save</button>
                                  </div>
                                )}
                                <div className="flex flex-wrap gap-1.5">
                                  <button data-testid="close-na-complete" onClick={() => patch({ next_action_status: "Completed" }, "Next action marked complete")} className="h-7 px-2.5 rounded-sm bg-ok text-white text-[11.5px] font-medium hover:opacity-90 transition-opacity flex items-center gap-1"><CheckCheck className="h-3 w-3" /> Mark Complete</button>
                                  <button data-testid="close-na-edit" onClick={() => { setNaEdit(job.next_action || ""); setNaResolve(naResolve === "close-edit2" ? "none" : "close-edit2"); }} className="h-7 px-2.5 rounded-sm border border-line bg-surface text-[11.5px] font-medium text-ink-2 hover:text-ink hover:border-steel transition-colors flex items-center gap-1"><Pencil className="h-3 w-3" /> Edit Next Action</button>
                                  <button data-testid="close-na-due" onClick={() => { setDueEdit(toInputDate(job.next_action_due_date)); setNaResolve(naResolve === "close-due2" ? "none" : "close-due2"); }} className="h-7 px-2.5 rounded-sm border border-line bg-surface text-[11.5px] font-medium text-ink-2 hover:text-ink hover:border-steel transition-colors flex items-center gap-1"><CalendarDays className="h-3 w-3" /> Change Due Date</button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <button
                    data-testid="mark-closed-btn"
                    onClick={closeJob}
                    disabled={!job.can_close}
                    className="w-full h-9 rounded-sm bg-ok text-white text-[13px] font-medium flex items-center justify-center gap-1.5 hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" /> Mark as Closed
                  </button>
                </div>
              </div>
            )}
          </Section>

          {/* Proof of Delivery */}
          <Section title="Proof of Delivery" icon={ShieldCheck}>
            <div className="flex items-center justify-between">
              <span className="text-[13.5px] text-ink">{pod?.type}</span>
              <span className="text-[12px] px-2 py-0.5 rounded-sm font-medium" style={{ color: (DOC_STATUS_STYLES[pod?.status] || {}).fg, backgroundColor: (DOC_STATUS_STYLES[pod?.status] || {}).bg }}>{pod?.status}</span>
            </div>
            <button data-testid="upload-pod-btn" onClick={() => setDoc("Proof of Delivery", "Uploaded")} className="mt-3 w-full h-9 rounded-sm border border-line bg-surface-alt text-ink text-[13px] font-medium hover:bg-line/40 transition-colors flex items-center justify-center gap-1.5">
              <Plus className="h-3.5 w-3.5" /> Simulate POD Upload
            </button>
          </Section>
          </>)}

          {/* Maritime delivery actions — replaces back-office widgets */}
          {meta.enable_interisland_transit && (
            <Section title="Delivery Actions" icon={ShieldCheck}>
              <div className="space-y-3">
                <button
                  data-testid="mark-delivered-btn"
                  disabled={closed}
                  onClick={() => patch(
                    { current_stage: job.current_stage === "Delivered" ? "In Transit" : "Delivered" },
                    job.current_stage === "Delivered" ? "Marked In Transit" : "Marked as Delivered",
                  )}
                  className={`w-full h-12 rounded-md border-2 border-[#0B132B] shadow-[2px_2px_0px_0px_#0B132B] font-black text-sm uppercase tracking-wide flex items-center justify-center gap-2 transition-all hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px] disabled:opacity-50 disabled:cursor-not-allowed ${job.current_stage === "Delivered" ? "bg-[#059669] text-white" : "bg-white text-[#0B132B]"}`}>
                  <CheckCircle2 className="h-4 w-4" /> {job.current_stage === "Delivered" ? "Delivered — tap to undo" : "Mark as Delivered"}
                </button>
                <a
                  data-testid="wa-group-share-btn"
                  href={`https://wa.me/?text=${encodeURIComponent(`📦 ${job.job_number} — ${job.cargo_description || "Shipment update"}${job.end_recipient_owner ? `\nDeliver directly to: ${job.end_recipient_owner}` : ""}${job.track_token ? `\nLive tracking: ${window.location.origin}/track/${job.track_token}` : ""}`)}`}
                  target="_blank" rel="noreferrer"
                  className="w-full h-12 rounded-md border-2 border-[#0B132B] shadow-[2px_2px_0px_0px_#0B132B] bg-[#0B132B] text-white font-black text-sm uppercase tracking-wide flex items-center justify-center gap-2 transition-all hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px]">
                  📢 Send Update to WhatsApp Group
                </a>
                <p className="text-[11px] text-ink-3 leading-snug">Auto-fills the cargo manifest and the live tracking verification link for quick sharing to your driver group.</p>
              </div>
            </Section>
          )}

          {/* Timeline */}
          <Section title="Activity Timeline" icon={History} label={`${job.timeline.length} events`}>
            <div className="space-y-3.5">
              {[...job.timeline].reverse().map((t) => (
                <div key={t.id} data-testid={`timeline-event-${t.type}`} className="flex gap-3">
                  <div className="flex flex-col items-center pt-1">
                    <span className="h-2 w-2 rounded-full bg-steel" />
                    <span className="w-px flex-1 bg-line mt-1" />
                  </div>
                  <div className="pb-1">
                    <p className="text-[13px] text-ink leading-snug">{t.message}</p>
                    <p className="text-[11px] text-ink-3 mt-0.5 font-mono-data">{t.actor} · {fmtDateTime(t.timestamp)}</p>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        </div>
      </div>
    </Layout>
  );
}
